'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { HUBSPOT_FORMS, HUBSPOT_PORTAL_ID, HUBSPOT_REGION } from '@/lib/constants';
import { HUBSPOT_SUBJECT_FIELD, HUBSPOT_V4_EVENTS, loadHubSpotEmbed } from '@/lib/hubspot';
import type { HubSpotV4EventDetail, HubSpotV4Form } from '@/lib/hubspot';
import type { HubSpotFormKey } from '@/lib/types';
import { Button } from '@/components/ui/Button';

export interface HubSpotFormMessages {
  loading: string;
  /** Shown once the form renders; empty string hides the status line. */
  ready: string;
  timeout: string;
  error: string;
  sent?: string;
  notSent?: string;
}

interface HubSpotFormProps {
  form: HubSpotFormKey;
  messages: HubSpotFormMessages;
  /** v4 only: prefill the ticket subject when the visitor left it empty. */
  subject?: string;
  className?: string;
  /** Status line above (default) or below the form (Market Briefs newsletter). */
  statusPlacement?: 'before' | 'after';
  /** Extra class on the status line (page-specific type size). */
  statusClassName?: string;
  /** Extra class on the form host (page-specific frame styling). */
  hostClassName?: string;
}

const READY_TIMEOUT_MS = { v2: 20000, v4: 25000 } as const;

type Phase = 'loading' | 'ready' | 'failed';

/**
 * Page-unique mount ids. `useId` is position-based, so a remounted form (the
 * dialog reopened, StrictMode, a retry) could reuse an id that a still-pending
 * `hbspt.forms.create` from the previous mount targets — rendering two forms.
 */
let mountSequence = 0;

/**
 * The one HubSpot native-form embed (signup dialog + Customer Support).
 * Handles both embed generations, slow loads, retry and submission status.
 */
export function HubSpotForm({
  form,
  messages,
  subject,
  className = '',
  statusPlacement = 'before',
  statusClassName = '',
  hostClassName = '',
}: HubSpotFormProps) {
  const config = HUBSPOT_FORMS[form];
  const hostRef = useRef<HTMLDivElement>(null);
  const instanceRef = useRef<HubSpotV4Form | null>(null);
  const submittedRef = useRef(false);
  const [attempt, setAttempt] = useState(0);
  const [phase, setPhase] = useState<Phase>('loading');
  const [status, setStatus] = useState(messages.loading);

  const hasForm = () => Boolean(hostRef.current?.querySelector('form, iframe'));

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;
    let active = true;
    // Started below once listeners exist; a ref-like box keeps it `const`.
    const timers: { ready?: number } = {};
    setPhase('loading');
    setStatus(messages.loading);
    host.replaceChildren();

    const ready = () => {
      if (!active) return;
      window.clearTimeout(timers.ready);
      observer.disconnect();
      setPhase('ready');
      setStatus(messages.ready);
    };
    const fail = (message: string) => {
      if (!active) return;
      // Modern embeds may replace the mount with an iframe: never discard a form.
      if (hasForm() && config.embed === 'v2') return ready();
      window.clearTimeout(timers.ready);
      observer.disconnect();
      setPhase('failed');
      setStatus(message);
    };
    const observer = new MutationObserver(() => {
      if (config.embed === 'v2' && hasForm()) ready();
    });

    const mount = document.createElement('div');
    mount.id = `cid-hubspot-${(mountSequence += 1)}`;
    if (config.embed === 'v4') {
      mount.className = 'hs-form-frame';
      mount.dataset.region = HUBSPOT_REGION;
      mount.dataset.formId = config.id;
      mount.dataset.portalId = HUBSPOT_PORTAL_ID;
    }
    host.appendChild(mount);

    const onV4Ready = (event: Event) => {
      const detail = (event as CustomEvent<HubSpotV4EventDetail>).detail;
      if (detail?.formId !== config.id || !host.contains(event.target as Node)) return;
      instanceRef.current = window.HubSpotFormsV4?.getFormFromEvent?.(event) ?? null;
      ready();
    };
    const onV4Result = (event: Event) => {
      const detail = (event as CustomEvent<HubSpotV4EventDetail>).detail;
      if (detail?.formId !== config.id || !host.contains(event.target as Node)) return;
      const sent = event.type === HUBSPOT_V4_EVENTS.success;
      if (sent) submittedRef.current = true;
      const message = sent ? messages.sent : messages.notSent;
      if (message && (sent || !submittedRef.current)) setStatus(message);
    };

    if (config.embed === 'v4') {
      // HubSpot requires listeners before the loader runs.
      window.addEventListener(HUBSPOT_V4_EVENTS.ready, onV4Ready);
      window.addEventListener(HUBSPOT_V4_EVENTS.success, onV4Result);
      window.addEventListener(HUBSPOT_V4_EVENTS.failed, onV4Result);
    }

    timers.ready = window.setTimeout(() => fail(messages.timeout), READY_TIMEOUT_MS[config.embed]);
    loadHubSpotEmbed(config.embed)
      .then(() => {
        if (!active || config.embed !== 'v2' || !mount.isConnected) return;
        observer.observe(host, { childList: true, subtree: true });
        window.hbspt?.forms?.create?.({
          region: HUBSPOT_REGION,
          portalId: HUBSPOT_PORTAL_ID,
          formId: config.id,
          target: `#${mount.id}`,
          onFormReady: ready,
          onFormSubmitted: () => {
            if (!active || !messages.sent) return;
            submittedRef.current = true;
            setStatus(messages.sent);
          },
        });
        if (hasForm()) ready();
      })
      .catch(() => fail(messages.error));

    return () => {
      active = false;
      window.clearTimeout(timers.ready);
      observer.disconnect();
      window.removeEventListener(HUBSPOT_V4_EVENTS.ready, onV4Ready);
      window.removeEventListener(HUBSPOT_V4_EVENTS.success, onV4Result);
      window.removeEventListener(HUBSPOT_V4_EVENTS.failed, onV4Result);
      instanceRef.current = null;
      // Detach this attempt's mount so a late create/render cannot land in it.
      mount.remove();
    };
    // Messages are static copy; reloading only on form/attempt changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, attempt]);

  const prefillSubject = useCallback(async (topic: string) => {
    const instance = instanceRef.current;
    if (!instance || submittedRef.current) return;
    try {
      const fields = await instance.getFormFieldValues();
      if (!fields.some((field) => field?.name === HUBSPOT_SUBJECT_FIELD)) return;
      const value = await instance.getFieldValue(HUBSPOT_SUBJECT_FIELD);
      if (instance !== instanceRef.current || submittedRef.current) return;
      if (value == null || (typeof value === 'string' && value.trim() === '')) {
        instance.setFieldValue(HUBSPOT_SUBJECT_FIELD, topic);
      }
    } catch {
      // Prefill is optional; the visitor can still type the subject.
    }
  }, []);

  useEffect(() => {
    if (subject && phase === 'ready') void prefillSubject(subject);
  }, [subject, phase, prefillSubject]);

  const statusLine = status && (
    <p
      className={`cid-hubspot-status ${statusClassName}`.trim()}
      role="status"
      aria-live="polite"
      data-state={phase === 'failed' ? 'error' : phase}
    >
      {status}
    </p>
  );

  return (
    <div className={className}>
      {statusPlacement === 'before' && statusLine}
      {phase === 'failed' && (
        <Button
          variant="secondary"
          className="cid-hubspot-retry"
          onClick={() => setAttempt((n) => n + 1)}
        >
          Try loading the form again
        </Button>
      )}
      {/* data-phase lets each embed style its failed state (signup hides the empty card). */}
      <div
        ref={hostRef}
        className={`cid-hubspot-host ${hostClassName}`.trim()}
        aria-busy={phase === 'loading'}
        data-phase={phase}
      />
      {statusPlacement === 'after' && statusLine}
    </div>
  );
}
